import { Link } from 'react-router';
import { cn } from '~/common/utils/styleUtil';
import { TgsArticleImage } from '~/common/components/ArticleImage';
import { ArticleSellUnit } from '~/features/catalog/components/article/ArticleSellUnit';
import { ArticlePrice } from '~/features/catalog/components/article/ArticlePrice';
import { ArticleSecondaryText } from '~/features/catalog/components/article/ArticleSecondaryText';
import { useArticleDetailPath } from '~/features/catalog/hooks/useArticleDetailPath';
import type { AghArticle } from '~/common/types/aghArticle';
import { AddToTemplate } from '~/features/catalog/components/AddToTemplate';
import { useCatalogAghArticle } from '~/features/catalog/hooks/useCatalogAghArticle';
import { CatalogAghArticleNotAvailable } from '~/features/catalog/components/CatalogAghArticleNotAvailable';
import { SalesOrderSetInfo } from '~/features/catalog/components/article/SalesOrderSetInfo';
import { PriceBadgeGroup } from '~/common/components/PriceBadgeGroup.tsx';
import { useGkAirTracking } from '~/features/catalog/routes/resources/GkAirTrackingRoute.tsx';

type CatalogListItemProps = {
  article: AghArticle;
  isAuthenticated: boolean;
  className?: string;
  isSingleItemInRow?: boolean;
};

export function CatalogAghTileItem({
  article,
  isAuthenticated,
  className,
  isSingleItemInRow = false
}: Readonly<CatalogListItemProps>) {
  const catalogArticle = useCatalogAghArticle(article);
  const articleDetailPath = useArticleDetailPath(catalogArticle.articleNumber);
  const trackGkAirClick = useGkAirTracking(catalogArticle.articleNumber, catalogArticle.gkAirTrackingToken);

  return (
    <article
      className={cn(
        'group hover:bg-neutral-50 h-full flex flex-col',
        className,
        isSingleItemInRow ? 'border-b pb-8' : 'p-4'
      )}
    >
      <Link
        id={catalogArticle.articleNumber}
        to={articleDetailPath}
        className={'flex flex-col justify-between flex-1 '}
        data-testid={`article-link-${article.articleNumber}`}
        state={{ quantity: catalogArticle.quantity }}
        onClick={() => (catalogArticle.gkAirTrackingToken ? trackGkAirClick() : undefined)}
      >
        <div className={'pb-8'}>
          <div className={'relative flex justify-center p-2 w-full'}>
            <TgsArticleImage
              articleNumber={catalogArticle.articleNumber}
              celumId={catalogArticle.celumId}
              alt={catalogArticle.description}
            />
            {catalogArticle.isInSalesOrderSet && (
              <SalesOrderSetInfo className={cn('absolute right-6', !isSingleItemInRow && 'right-0')} />
            )}
          </div>
          <ArticlePrice
            showOnlyActionPrice={true}
            price={catalogArticle.price}
            oldPrice={catalogArticle.oldPrice}
            isAction={catalogArticle.isAction}
            actionValidFrom={catalogArticle.actionValidFrom}
            actionValidTo={catalogArticle.actionValidTo}
            unitText={catalogArticle.unitText}
            isHit={catalogArticle.isHit}
          />
          <div
            className={cn(
              'pt-4 text-base font-bold line-clamp-2',
              catalogArticle.isAction && !catalogArticle.isHit && !catalogArticle.isFutureAction && 'text-primary'
            )}
            data-testid={`article-description-${catalogArticle.articleNumber}`}
          >
            {catalogArticle.description}
          </div>
          <ArticleSecondaryText
            articleNumber={catalogArticle.articleNumber}
            rohstoffHerkunft={catalogArticle.rohstoffHerkunft}
            brand={catalogArticle.brand}
            lineClamp={5}
            tile={true}
          />
          <PriceBadgeGroup
            direction="wrap"
            badges={[
              ...(catalogArticle.noveltyUntil ? [{ type: 'new' as const }] : []),
              ...(isAuthenticated && catalogArticle.isHit ? [{ type: 'hit' as const }] : [])
            ]}
          />
        </div>
      </Link>
      <div>
        <ArticleSellUnit
          showOnlyActionPrice={true}
          selectedArticle={catalogArticle}
          allArticles={catalogArticle.allArticles}
          onArticleChange={changeInfo => catalogArticle.setSelectedArticleNumber(changeInfo.articleNumber.toString())}
        />
      </div>
      <AddToTemplate
        className={'min-w-[200px]'}
        isAuthenticated={isAuthenticated}
        mainArticleId={catalogArticle.mainArticleId}
        articleNumber={catalogArticle.articleNumber}
        articleDescriptionText={catalogArticle.description}
        quantity={catalogArticle.quantity}
        onQuantityChange={catalogArticle.setQuantity}
        calculatedTotalPriceText={catalogArticle.calculatedTotalPriceText}
        cacheQuantity={true}
        forceSmallAddButton={true}
      />
      <CatalogAghArticleNotAvailable
        status={catalogArticle.status}
        showArticleNotAvailable={catalogArticle.showCurrentlyNotAvailableMessage}
        substituteArticleNumber={catalogArticle.substituteArticleNumber}
        substituteArticleText={catalogArticle.substituteArticleText}
      />
    </article>
  );
}
